// RoomOccupancyReport.jsx - Updated with Smart Date Handling and Improved Filters
import React, { useState, useEffect } from 'react';
import {
  FaHotel,
  FaBed,
  FaUsers,
  FaCalendarAlt,
  FaChartLine,
  FaDownload,
  FaEnvelope,
  FaPrint,
  FaSync,
  FaArrowUp,
  FaArrowDown,
  FaDollarSign,
  FaClock,
  FaBuilding,
  FaSearch,
  FaFilter,
  FaTimes,
  FaSpinner,
  FaUserCheck,
  FaChartPie,
  FaChartBar,
  FaFilePdf,
  FaFileExcel,
  FaCheck,
  FaUser,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-[#E8ECF2] rounded-xl shadow-lg p-4 min-w-[150px]">
        <p className="text-xs font-semibold text-[#6B7FA0] mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 py-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-xs text-[#4A5F7A]">{entry.name}:</span>
            </div>
            <span className="text-xs font-bold text-[#0F1A3A]">
              {formatter ? formatter(entry.value) : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Get current week number without modifying Date prototype
const getCurrentWeek = () => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const diff =
    (now - startOfYear + (startOfYear.getTimezoneOffset() - now.getTimezoneOffset()) * 60000) /
    86400000;
  return Math.ceil((diff + startOfYear.getDay() + 1) / 7);
};

const RoomOccupancyReport = () => {
  const [loading, setLoading] = useState(false);
  const [propertyId, setPropertyId] = useState(null);
  const [reportData, setReportData] = useState(null);

  // Filters
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dailyDate, setDailyDate] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBookingStatus, setSelectedBookingStatus] = useState('all');
  const [selectedBookingType, setSelectedBookingType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [emailExportFormat, setEmailExportFormat] = useState('pdf');
  const [emailWeek, setEmailWeek] = useState(getCurrentWeek() || 1);

  // Email Modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailRecipientName, setEmailRecipientName] = useState('');
  const [emailGeneratedBy, setEmailGeneratedBy] = useState('');
  const [emailPeriod, setEmailPeriod] = useState('monthly');
  const [emailYear, setEmailYear] = useState(new Date().getFullYear());
  const [emailMonth, setEmailMonth] = useState(new Date().getMonth() + 1);
  const [emailStartDate, setEmailStartDate] = useState('');
  const [emailEndDate, setEmailEndDate] = useState('');
  const [emailRoomType, setEmailRoomType] = useState('');
  const [emailRoomStatus, setEmailRoomStatus] = useState('');
  const [emailBookingStatus, setEmailBookingStatus] = useState('');
  const [emailBookingType, setEmailBookingType] = useState('');
  const [emailCcHotel, setEmailCcHotel] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  // PDF Modal
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPeriod, setPdfPeriod] = useState('monthly');
  const [pdfYear, setPdfYear] = useState(new Date().getFullYear());
  const [pdfMonth, setPdfMonth] = useState(new Date().getMonth() + 1);
  const [pdfStartDate, setPdfStartDate] = useState('');
  const [pdfEndDate, setPdfEndDate] = useState('');

  // Excel Modal
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelPeriod, setExcelPeriod] = useState('monthly');
  const [excelYear, setExcelYear] = useState(new Date().getFullYear());
  const [excelMonth, setExcelMonth] = useState(new Date().getMonth() + 1);
  const [excelStartDate, setExcelStartDate] = useState('');
  const [excelEndDate, setExcelEndDate] = useState('');
  const [excelRoomType, setExcelRoomType] = useState('');
  const [excelRoomStatus, setExcelRoomStatus] = useState('');
  const [excelBookingStatus, setExcelBookingStatus] = useState('');
  const [excelBookingType, setExcelBookingType] = useState('');

  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  // Check if all required dates are filled for the selected period
  const isDateValid = () => {
    if (selectedPeriod === 'monthly') return true;
    if (selectedPeriod === 'yearly') return true;
    if (selectedPeriod === 'daily') return dailyDate !== '';
    if (selectedPeriod === 'weekly') return weekStartDate !== '' && weekEndDate !== '';
    if (selectedPeriod === 'custom') return startDate !== '' && endDate !== '';
    return false;
  };

  // Get current user and property_id
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${AQ_BASE_URL}/aq/api/currentuser`, {
          withCredentials: true,
          headers: { accept: 'application/json' },
        });

        if (response.data && response.data.property_id) {
          setPropertyId(response.data.property_id);
          if (response.data.email) {
            setUserEmail(response.data.email);
            setEmailGeneratedBy(response.data.email);
          }
          if (response.data.name) setUserName(response.data.name);
        } else {
          throw new Error('Property ID not found in user data');
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
        toast.error('Failed to load user information. Please ensure you are logged in.');
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch report data with smart date validation
  const fetchReportData = async () => {
    // Don't fetch if dates are invalid
    if (!isDateValid()) {
      toast.warning('Please fill in all required date fields');
      return;
    }

    setLoading(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/occupancy`;

      const params = new URLSearchParams();

      if (selectedPeriod === 'monthly' && month) {
        params.append('year', year);
        params.append('month', month);
      } else if (selectedPeriod === 'yearly') {
        params.append('year', year);
        // Auto-calculate year range for yearly
        const startOfYear = `${year}-01-01`;
        const endOfYear = `${year}-12-31`;
        params.append('start_date', startOfYear);
        params.append('end_date', endOfYear);
      } else if (selectedPeriod === 'daily') {
        if (dailyDate) {
          params.append('start_date', dailyDate);
          params.append('end_date', dailyDate);
        }
      } else if (selectedPeriod === 'weekly') {
        if (weekStartDate && weekEndDate) {
          params.append('start_date', weekStartDate);
          params.append('end_date', weekEndDate);
        }
      } else if (selectedPeriod === 'custom') {
        if (startDate && endDate) {
          params.append('start_date', startDate);
          params.append('end_date', endDate);
        }
      }

      // Add filters
      if (selectedRoomType && selectedRoomType !== 'all') {
        params.append('room_type', selectedRoomType);
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.append('room_status', selectedStatus);
      }
      if (selectedBookingStatus && selectedBookingStatus !== 'all') {
        params.append('booking_status', selectedBookingStatus);
      }
      if (selectedBookingType && selectedBookingType !== 'all') {
        params.append('booking_type', selectedBookingType);
      }

      if (propertyId) params.append('property_id', propertyId);

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      console.log('Fetching URL:', url);

      const response = await axios.get(url, {
        withCredentials: true,
        headers: { accept: 'application/json' },
      });

      if (response.data && response.data.success) {
        setReportData(response.data.data);
        toast.success('Report loaded successfully!');
      } else {
        throw new Error('Failed to fetch report data');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      toast.error(err.response?.data?.detail || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Handle period change - reset dates and fetch immediately for monthly/yearly
  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod);

    // Reset date states based on period
    if (newPeriod === 'monthly' || newPeriod === 'yearly') {
      // Auto-fetch for monthly/yearly
      setTimeout(() => {
        fetchReportData();
      }, 100);
    } else if (newPeriod === 'daily') {
      setDailyDate('');
    } else if (newPeriod === 'weekly') {
      setWeekStartDate('');
      setWeekEndDate('');
    } else if (newPeriod === 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  // Handle date changes - only set values, don't fetch immediately
  const handleDateChange = (type, value) => {
    switch (type) {
      case 'daily':
        setDailyDate(value);
        break;
      case 'weekStart':
        setWeekStartDate(value);
        break;
      case 'weekEnd':
        setWeekEndDate(value);
        break;
      case 'customStart':
        setStartDate(value);
        break;
      case 'customEnd':
        setEndDate(value);
        break;
      default:
        break;
    }
  };

  // Handle applying date range - manual fetch trigger
  const handleApplyDateRange = () => {
    if (isDateValid()) {
      fetchReportData();
    } else {
      toast.warning('Please fill in all required date fields');
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    if (propertyId !== null) {
      fetchReportData();
    }
  }, [propertyId]);

  // Fetch when filters change (but not on date changes)
  useEffect(() => {
    if (propertyId !== null && isDateValid()) {
      fetchReportData();
    }
  }, [selectedRoomType, selectedStatus, selectedBookingStatus, selectedBookingType]);

  // Separate effect for year/month changes (monthly/yearly auto-fetch)
  useEffect(() => {
    if (propertyId !== null && (selectedPeriod === 'monthly' || selectedPeriod === 'yearly')) {
      fetchReportData();
    }
  }, [year, month, selectedPeriod]);

  // PDF Download Handler
  const handleDownloadPDF = async () => {
    setLoading(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/occupancy/pdf`;

      const params = new URLSearchParams();
      params.append('generated_by', encodeURIComponent(userName || 'Hotel Manager'));

      if (pdfPeriod === 'monthly' && pdfMonth) {
        params.append('year', pdfYear);
        params.append('month', pdfMonth);
      } else if (pdfPeriod === 'yearly') {
        params.append('year', pdfYear);
      } else if (pdfPeriod === 'daily') {
        if (pdfStartDate) {
          params.append('start_date', pdfStartDate);
          params.append('end_date', pdfStartDate);
        }
      } else if (pdfPeriod === 'weekly') {
        if (pdfStartDate && pdfEndDate) {
          params.append('start_date', pdfStartDate);
          params.append('end_date', pdfEndDate);
        }
      } else if (pdfPeriod === 'custom') {
        if (pdfStartDate && pdfEndDate) {
          params.append('start_date', pdfStartDate);
          params.append('end_date', pdfEndDate);
        }
      }

      // Add filters for PDF
      if (selectedRoomType && selectedRoomType !== 'all') {
        params.append('room_type', encodeURIComponent(selectedRoomType));
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.append('room_status', encodeURIComponent(selectedStatus));
      }
      if (selectedBookingStatus && selectedBookingStatus !== 'all') {
        params.append('booking_status', encodeURIComponent(selectedBookingStatus));
      }
      if (selectedBookingType && selectedBookingType !== 'all') {
        params.append('booking_type', encodeURIComponent(selectedBookingType));
      }

      if (propertyId) params.append('property_id', propertyId);
      if (userEmail) params.append('email', encodeURIComponent(userEmail));

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `OccupancyReport_${pdfPeriod}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('PDF downloaded successfully!');
      setShowPdfModal(false);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast.error('Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  // Excel Download Handler
  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/occupancy/excel`;

      const params = new URLSearchParams();

      if (excelPeriod === 'monthly' && excelMonth) {
        params.append('year', excelYear);
        params.append('month', excelMonth);
      } else if (excelPeriod === 'yearly') {
        params.append('year', excelYear);
      } else if (excelPeriod === 'daily') {
        if (excelStartDate) {
          params.append('start_date', excelStartDate);
          params.append('end_date', excelStartDate);
        }
      } else if (excelPeriod === 'weekly') {
        if (excelStartDate && excelEndDate) {
          params.append('start_date', excelStartDate);
          params.append('end_date', excelEndDate);
        }
      } else if (excelPeriod === 'custom') {
        if (excelStartDate && excelEndDate) {
          params.append('start_date', excelStartDate);
          params.append('end_date', excelEndDate);
        }
      }

      // Add filters for Excel
      if (excelRoomType && excelRoomType !== 'all') {
        params.append('room_type', encodeURIComponent(excelRoomType));
      }
      if (excelRoomStatus && excelRoomStatus !== 'all') {
        params.append('room_status', encodeURIComponent(excelRoomStatus));
      }
      if (excelBookingStatus && excelBookingStatus !== 'all') {
        params.append('booking_status', encodeURIComponent(excelBookingStatus));
      }
      if (excelBookingType && excelBookingType !== 'all') {
        params.append('booking_type', encodeURIComponent(excelBookingType));
      }

      if (propertyId) params.append('property_id', propertyId);

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `OccupancyReport_${excelPeriod}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Excel report downloaded successfully!');
      setShowExcelModal(false);
    } catch (err) {
      console.error('Error downloading Excel:', err);
      toast.error('Failed to download Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipient) {
      toast.error('Please enter recipient email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailRecipient)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/occupancy/send-email`;

      const params = new URLSearchParams();

      // IMPORTANT: Don't encode emails - URLSearchParams handles it
      params.append('export_format', emailExportFormat);
      params.append('recipient_email', emailRecipient); // Remove encodeURIComponent
      params.append('recipient_name', emailRecipientName || 'Hotel Manager');
      params.append('generated_by', emailGeneratedBy || userEmail || 'Hotel Manager');

      if (emailPeriod === 'monthly' && emailMonth) {
        params.append('period', 'monthly'); // Add period parameter
        params.append('year', emailYear);
        params.append('month', emailMonth);
      } else if (emailPeriod === 'yearly') {
        params.append('period', 'yearly'); // Add period parameter
        params.append('year', emailYear);
      } else if (emailPeriod === 'daily') {
        if (emailStartDate) {
          params.append('period', 'daily'); // Add period parameter
          params.append('start_date', emailStartDate);
          params.append('end_date', emailStartDate);
        }
      } else if (emailPeriod === 'weekly') {
        if (emailStartDate && emailEndDate) {
          params.append('period', 'weekly'); // Add period parameter
          params.append('start_date', emailStartDate);
          params.append('end_date', emailEndDate);
        }
      } else if (emailPeriod === 'custom') {
        if (emailStartDate && emailEndDate) {
          params.append('period', 'custom'); // Add period parameter
          params.append('start_date', emailStartDate);
          params.append('end_date', emailEndDate);
        }
      }

      // Add filters - remove encodeURIComponent
      if (emailRoomType && emailRoomType !== 'all') {
        params.append('room_type', emailRoomType);
      }
      if (emailRoomStatus && emailRoomStatus !== 'all') {
        params.append('room_status', emailRoomStatus);
      }
      if (emailBookingStatus && emailBookingStatus !== 'all') {
        params.append('booking_status', emailBookingStatus);
      }
      if (emailBookingType && emailBookingType !== 'all') {
        params.append('booking_type', emailBookingType);
      }

      if (propertyId) params.append('property_id', propertyId);
      if (emailCcHotel) params.append('cc_hotel', 'true');

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      console.log('Sending email URL:', url); // Debug log
      console.log('Recipient email:', emailRecipient); // Debug log

      const response = await axios.post(url, null, {
        withCredentials: true,
        headers: { accept: 'application/json' },
      });

      if (response.data && response.data.success) {
        toast.success(`Report sent to ${emailRecipient} successfully!`);
        setShowEmailModal(false);
        setEmailRecipient('');
        setEmailRecipientName('');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send email report');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      Occupied: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      Available: 'bg-blue-100 text-blue-700 border-blue-200',
      Vacant: 'bg-blue-100 text-blue-700 border-blue-200',
      Reserved: 'bg-purple-100 text-purple-700 border-purple-200',
      'Under Maintenance': 'bg-red-100 text-red-700 border-red-200',
      Maintenance: 'bg-red-100 text-red-700 border-red-200',
      Unavailable: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusColorHex = (status) => {
    const colors = {
      Occupied: '#0F1A3A',
      Available: '#2A4580',
      Reserved: '#C9A84C',
      'Under Maintenance': '#DC2626',
      Unavailable: '#6B7FA0',
    };
    return colors[status] || '#6B7FA0';
  };

  const getStatusLabel = (status) => {
    const labels = {
      Occupied: 'Occupied Rooms',
      Available: 'Available Rooms',
      Reserved: 'Reserved Rooms',
      'Under Maintenance': 'Under Maintenance',
      Unavailable: 'Unavailable Rooms',
    };
    return labels[status] || status;
  };

  // Get KPI from API
  const getKPI = () => {
    if (!reportData?.kpi) {
      return {
        total_rooms: 0,
        occupied_rooms: 0,
        available_rooms: 0,
        maintenance_rooms: 0,
        occupancy_rate_pct: 0,
        adr: 0,
        revpar: 0,
        total_revenue: 0,
        avg_length_of_stay: 0,
        active_bookings: 0,
        walkin_bookings: 0,
        online_bookings: 0,
      };
    }
    return reportData.kpi;
  };

  const getRoomStatusList = () => {
    if (!reportData?.room_status_list) return [];
    return reportData.room_status_list;
  };

  const getRoomTypeStats = () => {
    if (!reportData?.room_type_stats) return [];
    return reportData.room_type_stats;
  };

  const getStatusDistribution = () => {
    if (!reportData?.status_distribution) return [];
    return reportData.status_distribution;
  };

  const getWeeklyTrend = () => {
    if (!reportData?.weekly_trend) return [];
    return reportData.weekly_trend;
  };

  const getBookingTypeStats = () => {
    if (!reportData?.booking_type_stats) return [];
    return reportData.booking_type_stats;
  };

  const kpi = getKPI();
  const roomStatusList = getRoomStatusList();
  const roomTypeStats = getRoomTypeStats();
  const statusDistribution = getStatusDistribution();
  const weeklyTrend = getWeeklyTrend();
  const bookingTypeStats = getBookingTypeStats();

  // Reset filters
  const resetFilters = () => {
    setSelectedRoomType('all');
    setSelectedStatus('all');
    setSelectedBookingStatus('all');
    setSelectedBookingType('all');
    setSearchTerm('');
    toast.info('Filters reset');
  };

  // Filter rooms
  const filteredRooms = roomStatusList.filter((room) => {
    let match = true;

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      const roomMatch =
        room.room_number?.toString().includes(search) ||
        room.room_type?.toLowerCase().includes(search) ||
        room.guest_name?.toLowerCase().includes(search) ||
        room.booking_id?.toLowerCase().includes(search) ||
        room.booking_type?.toLowerCase().includes(search);
      if (!roomMatch) match = false;
    }

    if (
      selectedRoomType !== 'all' &&
      room.room_type?.toLowerCase() !== selectedRoomType.toLowerCase()
    ) {
      match = false;
    }

    if (
      selectedStatus !== 'all' &&
      room.room_status?.toLowerCase() !== selectedStatus.toLowerCase()
    ) {
      match = false;
    }

    return match;
  });

  // Prepare data for charts
  const roomTypeOccupancyData = roomTypeStats.map((type) => ({
    name: type.room_type.length > 15 ? type.room_type.substring(0, 15) + '...' : type.room_type,
    fullName: type.room_type,
    Occupied: type.occupied || 0,
    Available: type.available || 0,
    Maintenance: type.maintenance || 0,
    occupancy_pct: type.occupancy_pct || 0,
  }));

  const statusDistributionData = statusDistribution
    .filter((s) => s.count > 0)
    .map((item) => ({
      ...item,
      displayName: getStatusLabel(item.status),
    }));

  if (loading && !reportData) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#0F1A3A] border-t-[#C9A84C] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A5F7A] text-sm font-medium">Loading occupancy report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA] p-6 font-sans">
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gradient-to-r from-[#0F1A3A] to-[#1B2F5C] rounded-3xl p-6 shadow-2xl">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#C9A84C] rounded-2xl flex items-center justify-center shadow-lg">
              <FaHotel className="text-[#0F1A3A] text-3xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">
                Room Occupancy Report
              </h1>
              <p className="text-[#C9A84C] text-sm font-medium mt-0.5">
                {reportData?.period_label || 'Loading...'} • {reportData?.date_from || ''} to{' '}
                {reportData?.date_to || ''}
              </p>
              {userEmail && (
                <p className="text-xs text-[#6B7FA0] mt-0.5 flex items-center gap-1">
                  <FaUserCheck size={10} /> {userEmail}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchReportData}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all text-white text-sm font-medium border border-white/20"
            >
              <FaSync className={loading ? 'animate-spin' : ''} size={12} />
              Refresh
            </button>
            {/* <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all text-white text-sm font-medium border border-white/20"
            >
              <FaPrint size={12} />
              Print
            </button> */}
            <button
              onClick={() => {
                setExcelPeriod(selectedPeriod);
                setExcelYear(year);
                setExcelMonth(month);
                setExcelStartDate(startDate);
                setExcelEndDate(endDate);
                setExcelRoomType(selectedRoomType);
                setExcelRoomStatus(selectedStatus);
                setExcelBookingStatus(selectedBookingStatus);
                setExcelBookingType(selectedBookingType);
                setShowExcelModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#0F1A3A] text-white rounded-xl hover:bg-[#1B2F5C] transition-all text-sm font-medium border border-[#C9A84C]/30 shadow-lg hover:shadow-xl"
            >
              <FaFileExcel size={12} />
              Excel
            </button>
            <button
              onClick={() => {
                setEmailPeriod(selectedPeriod);
                setEmailYear(year);
                setEmailMonth(month);
                setEmailStartDate(startDate);
                setEmailEndDate(endDate);
                setEmailRoomType(selectedRoomType);
                setEmailRoomStatus(selectedStatus);
                setEmailBookingStatus(selectedBookingStatus);
                setEmailBookingType(selectedBookingType);
                setShowEmailModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#C9A84C] rounded-xl hover:bg-[#B8973A] transition-all text-[#0F1A3A] text-sm font-medium shadow-lg hover:shadow-xl"
            >
              <FaEnvelope size={12} />
              Email
            </button>
            <button
              onClick={() => {
                setPdfPeriod(selectedPeriod);
                setPdfYear(year);
                setPdfMonth(month);
                setPdfStartDate(startDate);
                setPdfEndDate(endDate);
                setShowPdfModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl hover:bg-[#C9A84C] transition-all text-[#0F1A3A] text-sm font-medium shadow-lg hover:shadow-xl"
            >
              <FaFilePdf size={12} />
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Period Filter - Improved with Smart Date Handling */}
      <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 border border-[#E8ECF2]">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-[#C9A84C]" />
            <span className="text-sm font-medium text-[#4A5F7A]">Period:</span>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
          >
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="custom">Custom Range</option>
          </select>

          {selectedPeriod === 'monthly' && (
            <>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
              >
                {[
                  'Jan',
                  'Feb',
                  'Mar',
                  'Apr',
                  'May',
                  'Jun',
                  'Jul',
                  'Aug',
                  'Sep',
                  'Oct',
                  'Nov',
                  'Dec',
                ].map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </>
          )}

          {selectedPeriod === 'yearly' && (
            <>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <span className="text-xs text-[#6B7FA0]">
                (Full year: {year}-01-01 to {year}-12-31)
              </span>
            </>
          )}

          {selectedPeriod === 'daily' && (
            <>
              <input
                type="date"
                value={dailyDate}
                onChange={(e) => handleDateChange('daily', e.target.value)}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
              />
              <button
                onClick={handleApplyDateRange}
                disabled={!dailyDate}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  dailyDate
                    ? 'bg-[#C9A84C] text-[#0F1A3A] hover:bg-[#B8973A] shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </>
          )}

          {selectedPeriod === 'weekly' && (
            <>
              <input
                type="date"
                value={weekStartDate}
                onChange={(e) => handleDateChange('weekStart', e.target.value)}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                placeholder="Week Start"
              />
              <span className="text-[#6B7FA0]">to</span>
              <input
                type="date"
                value={weekEndDate}
                onChange={(e) => handleDateChange('weekEnd', e.target.value)}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                placeholder="Week End"
              />
              <button
                onClick={handleApplyDateRange}
                disabled={!weekStartDate || !weekEndDate}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  weekStartDate && weekEndDate
                    ? 'bg-[#C9A84C] text-[#0F1A3A] hover:bg-[#B8973A] shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </>
          )}

          {selectedPeriod === 'custom' && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange('customStart', e.target.value)}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                placeholder="Start Date"
              />
              <span className="text-[#6B7FA0]">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange('customEnd', e.target.value)}
                className="px-4 py-2 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                placeholder="End Date"
              />
              <button
                onClick={handleApplyDateRange}
                disabled={!startDate || !endDate}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  startDate && endDate
                    ? 'bg-[#C9A84C] text-[#0F1A3A] hover:bg-[#B8973A] shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter Dropdown - Improved */}
      <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 border border-[#E8ECF2]">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0F1A3A] text-white rounded-xl hover:bg-[#1B2F5C] transition-all text-sm font-medium"
          >
            <FaFilter size={12} />
            Filters
            {(selectedRoomType !== 'all' ||
              selectedStatus !== 'all' ||
              selectedBookingStatus !== 'all' ||
              selectedBookingType !== 'all') && (
              <span className="w-2 h-2 bg-[#C9A84C] rounded-full animate-pulse"></span>
            )}
          </button>

          {showFilterDropdown && (
            <div className="w-full mt-3 grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-[#F8F9FB] rounded-xl border border-[#E8ECF2] animate-fadeIn">
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider block mb-2">
                  Room Type
                </label>
                <select
                  value={selectedRoomType}
                  onChange={(e) => setSelectedRoomType(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                >
                  <option value="all">All Types</option>
                  {roomTypeStats.map((type) => (
                    <option key={type.room_type} value={type.room_type}>
                      {type.room_type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider block mb-2">
                  Room Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                >
                  <option value="all">All Status</option>
                  <option value="Occupied">Occupied</option>
                  <option value="Available">Available</option>
                  <option value="Reserved">Reserved</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider block mb-2">
                  Booking Status
                </label>
                <select
                  value={selectedBookingStatus}
                  onChange={(e) => setSelectedBookingStatus(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                >
                  <option value="all">All Booking Status</option>
                  <option value="Soft">Reserved</option>
                  <option value="Hard">Checked In</option>
                  <option value="Checkedout">Checked Out</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider block mb-2">
                  Booking Type
                </label>
                <select
                  value={selectedBookingType}
                  onChange={(e) => setSelectedBookingType(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                >
                  <option value="all">All Booking Types</option>
                  <option value="Walkin">Walkin</option>
                  <option value="Online">Online</option>
                </select>
              </div>

              {(selectedRoomType !== 'all' ||
                selectedStatus !== 'all' ||
                selectedBookingStatus !== 'all' ||
                selectedBookingType !== 'all') && (
                <div className="md:col-span-4 flex justify-end">
                  <button
                    onClick={resetFilters}
                    className="px-4 py-2 text-sm font-medium text-[#6B7FA0] hover:text-[#0F1A3A] transition-colors flex items-center gap-2"
                  >
                    <FaTimes size={12} />
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards - Same as before */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* ... keep all KPI cards same ... */}
        <div className="bg-gradient-to-br from-[#0F1A3A] to-[#1B2F5C] rounded-2xl p-5 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#C9A84C] uppercase tracking-wider">
                Total Rooms
              </p>
              <p className="text-2xl font-bold text-white mt-1.5">{kpi.total_rooms || 0}</p>
              <p className="text-xs text-[#6B7FA0] mt-1.5">
                {kpi.maintenance_rooms || 0} under maintenance
              </p>
            </div>
            <div className="w-12 h-12 bg-[#C9A84C]/20 rounded-2xl flex items-center justify-center">
              <FaHotel className="text-[#C9A84C] text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                Occupied Rooms
              </p>
              <p className="text-2xl font-bold text-[#0F1A3A] mt-1.5">{kpi.occupied_rooms || 0}</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <FaArrowUp className="text-emerald-500" size={10} />
                <span className="text-xs font-medium text-emerald-600">
                  {kpi.occupancy_rate_pct || 0}% occupancy
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-[#0F1A3A]/10 rounded-2xl flex items-center justify-center">
              <FaUsers className="text-[#0F1A3A] text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                Available Rooms
              </p>
              <p className="text-2xl font-bold text-[#0F1A3A] mt-1.5">{kpi.available_rooms || 0}</p>
              <p className="text-xs text-[#6B7FA0] mt-1.5">{kpi.reserved_rooms || 0} reserved</p>
            </div>
            <div className="w-12 h-12 bg-[#C9A84C]/10 rounded-2xl flex items-center justify-center">
              <FaBed className="text-[#C9A84C] text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                Occupancy Rate
              </p>
              <p className="text-2xl font-bold text-[#0F1A3A] mt-1.5">
                {kpi.occupancy_rate_pct || 0}%
              </p>
              <div className="w-full bg-[#F0F2F5] rounded-full h-1.5 mt-2">
                <div
                  className="bg-[#0F1A3A] rounded-full h-1.5 transition-all duration-700"
                  style={{ width: `${kpi.occupancy_rate_pct || 0}%` }}
                />
              </div>
            </div>
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center">
              <FaChartLine className="text-[#D4AF37] text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 shadow-lg border border-[#E8ECF2]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F0F2F5] rounded-xl flex items-center justify-center">
              <FaDollarSign className="text-[#0F1A3A] text-sm" />
            </div>
            <div>
              <p className="text-xs text-[#6B7FA0] font-medium">ADR (Average Daily Rate)</p>
              <p className="text-lg font-bold text-[#0F1A3A]">{formatCurrency(kpi.adr || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-lg border border-[#E8ECF2]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F0F2F5] rounded-xl flex items-center justify-center">
              <FaChartLine className="text-[#0F1A3A] text-sm" />
            </div>
            <div>
              <p className="text-xs text-[#6B7FA0] font-medium">RevPAR</p>
              <p className="text-lg font-bold text-[#0F1A3A]">{formatCurrency(kpi.revpar || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-lg border border-[#E8ECF2]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F0F2F5] rounded-xl flex items-center justify-center">
              <FaClock className="text-[#0F1A3A] text-sm" />
            </div>
            <div>
              <p className="text-xs text-[#6B7FA0] font-medium">Avg Stay</p>
              <p className="text-lg font-bold text-[#0F1A3A]">{kpi.avg_length_of_stay || 0} days</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-lg border border-[#E8ECF2]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F0F2F5] rounded-xl flex items-center justify-center">
              <FaBuilding className="text-[#0F1A3A] text-sm" />
            </div>
            <div>
              <p className="text-xs text-[#6B7FA0] font-medium">Total Revenue</p>
              <p className="text-lg font-bold text-[#0F1A3A]">
                {formatCurrency(kpi.total_revenue || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Room Status Table - Same as before */}
      <div className="bg-white rounded-2xl border border-[#E8ECF2] overflow-hidden shadow-lg hover:shadow-xl transition-all mb-6">
        <div className="px-5 py-4 border-b border-[#E8ECF2] bg-[#F8F9FB]">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B7FA0] text-sm" />
              <input
                type="text"
                placeholder="Search by room, guest, booking..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] text-sm bg-white text-[#0F1A3A]"
              />
            </div>
            {(searchTerm || selectedRoomType !== 'all' || selectedStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRoomType('all');
                  setSelectedStatus('all');
                }}
                className="px-3 py-2 text-sm text-[#6B7FA0] hover:text-[#0F1A3A] transition flex items-center gap-1"
              >
                <FaTimes size={12} /> Clear
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E8ECF2]">
            <thead>
              <tr className="bg-[#F8F9FB]">
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Room
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Guest
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Guest Type
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Check-in
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Check-out
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Booking Type
                </th>
                <th className="px-5 py-2.5 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Booking Status
                </th>
                <th className="px-5 py-2.5 text-right text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-5 py-2.5 text-right text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Discount
                </th>
                {/* ✅ NEW: Final Amount column */}
                <th className="px-5 py-2.5 text-right text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Final Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E8ECF2]">
              {filteredRooms.length > 0 ? (
                filteredRooms.map((room, idx) => (
                  <tr key={idx} className="hover:bg-[#F8F9FB] transition-colors">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="text-sm font-bold text-[#0F1A3A]">{room.room_number}</span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#4A5F7A]">{room.room_type}</span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(room.room_status)}`}
                      >
                        {room.room_status}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#4A5F7A] capitalize">
                        {room.guest_name || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {room.is_corporate ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                          Corporate
                        </span>
                      ) : room.guest_name ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                          Leisure
                        </span>
                      ) : (
                        <span className="text-sm text-[#6B7FA0]">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {room.checkin_date || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {room.checkout_date || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {room.booking_type || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {room.booking_status ? (
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                            room.booking_status === 'Hard'
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : room.booking_status === 'Soft'
                                ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                          }`}
                        >
                          {room.booking_status === 'Hard'
                            ? 'Checked In'
                            : room.booking_status === 'Soft'
                              ? 'Reserved'
                              : room.booking_status}
                        </span>
                      ) : (
                        <span className="text-sm text-[#6B7FA0]">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <span className="text-sm font-bold text-[#0F1A3A]">
                        {room.rate > 0 ? formatCurrency(room.rate) : '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <span
                        className={`text-sm font-medium ${room.discount > 0 ? 'text-green-600' : 'text-gray-400'}`}
                      >
                        {room.discount > 0 ? formatCurrency(room.discount) : '-'}
                      </span>
                    </td>
                    {/* ✅ NEW: Final Amount column - shows final amount if > 0 */}
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <span className="text-sm font-bold text-[#0F1A3A]">
                        {room.final_amount > 0 ? formatCurrency(room.final_amount) : '-'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="px-5 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                  >
                    No rooms found for the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts Section - Same as before */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Weekly Trend */}
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#0F1A3A] flex items-center gap-2">
              <FaChartLine className="text-[#C9A84C]" /> Weekly Occupancy Trend
            </h3>
          </div>
          {weeklyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={weeklyTrend}>
                <defs>
                  <linearGradient id="occupiedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0F1A3A" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0F1A3A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F5" strokeWidth={0.5} />
                <XAxis
                  dataKey="label"
                  stroke="#6B7FA0"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#6B7FA0"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => Math.round(value)}
                />
                <Tooltip content={<CustomTooltip formatter={(value) => Math.round(value)} />} />
                <Area
                  type="monotone"
                  dataKey="occupied"
                  stroke="#0F1A3A"
                  strokeWidth={2.5}
                  fill="url(#occupiedGradient)"
                  name="Occupied"
                />
                <Area
                  type="monotone"
                  dataKey="available"
                  stroke="#8A9FC0"
                  strokeWidth={2.5}
                  fill="url(#occupiedGradient)"
                  name="Available"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-[#6B7FA0] text-sm font-medium">
              No trend data available
            </div>
          )}
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#0F1A3A] flex items-center gap-2">
              <FaChartPie className="text-[#C9A84C]" /> Status Distribution
            </h3>
          </div>
          {statusDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={statusDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="count"
                  nameKey="displayName"
                  label={({ displayName, percent }) =>
                    `${displayName} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={true}
                >
                  {statusDistributionData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getStatusColorHex(entry.status)}
                      stroke="white"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => {
                    const item = statusDistributionData.find(
                      (d) => d.status === name || d.displayName === name
                    );
                    return [
                      `${value} rooms (${item?.percentage || 0}%)`,
                      item?.displayName || name,
                    ];
                  }}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E8ECF2',
                    borderRadius: '12px',
                    padding: '8px 12px',
                    fontSize: '12px',
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-[#6B7FA0] text-sm font-medium">
              No status data available
            </div>
          )}
        </div>
      </div>

      {/* Room Type Occupancy */}
      <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#0F1A3A] flex items-center gap-2">
            <FaChartBar className="text-[#C9A84C]" /> Room Type Occupancy Breakdown
          </h3>
        </div>
        {roomTypeOccupancyData.length > 0 ? (
          <ResponsiveContainer
            width="100%"
            height={Math.max(300, roomTypeOccupancyData.length * 40)}
          >
            <BarChart
              data={roomTypeOccupancyData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F0F2F5"
                strokeWidth={0.5}
                horizontal={false}
              />
              <XAxis
                type="number"
                stroke="#6B7FA0"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="#6B7FA0"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={100}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0]?.payload;
                    return (
                      <div className="bg-white border border-[#E8ECF2] rounded-xl shadow-lg p-4 min-w-[180px]">
                        <p className="text-xs font-semibold text-[#0F1A3A] mb-2">
                          {data?.fullName || label}
                        </p>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-[#4A5F7A]">Occupied:</span>
                            <span className="font-bold text-[#0F1A3A]">{data?.Occupied || 0}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-[#4A5F7A]">Available:</span>
                            <span className="font-bold text-[#0F1A3A]">{data?.Available || 0}</span>
                          </div>
                          <div className="flex justify-between text-xs pt-1 border-t border-[#E8ECF2]">
                            <span className="text-[#4A5F7A]">Occupancy Rate:</span>
                            <span className="font-bold text-[#C9A84C]">
                              {data?.occupancy_pct || 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="Occupied" stackId="a" fill="#0F1A3A" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Available" stackId="a" fill="#2A4580" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Maintenance" stackId="a" fill="#DC2626" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px] text-[#6B7FA0] text-sm font-medium">
            No room type data available
          </div>
        )}
      </div>

      {/* Booking Type Stats - Keep the same */}
      {bookingTypeStats.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all">
            <h3 className="text-sm font-bold text-[#0F1A3A] flex items-center gap-2 mb-4">
              <FaChartBar className="text-[#C9A84C]" /> Booking Type Distribution
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={bookingTypeStats}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#F0F2F5"
                  strokeWidth={0.5}
                  horizontal={true}
                />
                <XAxis
                  type="number"
                  stroke="#6B7FA0"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="booking_type"
                  stroke="#6B7FA0"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E8ECF2',
                    borderRadius: '12px',
                    padding: '10px 14px',
                    fontSize: '12px',
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="#0F1A3A"
                  radius={[0, 4, 4, 0]}
                  label={{
                    position: 'right',
                    formatter: (value) => `${value}`,
                    fontSize: 11,
                    fill: '#4A5F7A',
                    fontWeight: 500,
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E8ECF2] hover:shadow-xl transition-all">
            <h3 className="text-sm font-bold text-[#0F1A3A] flex items-center gap-2 mb-4">
              <FaChartBar className="text-[#C9A84C]" /> Revenue by Booking Type
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={bookingTypeStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F5" strokeWidth={0.5} />
                <XAxis
                  dataKey="booking_type"
                  stroke="#6B7FA0"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#6B7FA0"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E8ECF2',
                    borderRadius: '12px',
                    padding: '8px 12px',
                    fontSize: '12px',
                  }}
                />
                <Bar
                  dataKey="revenue"
                  fill="#C9A84C"
                  name="Revenue"
                  radius={[4, 4, 0, 0]}
                  label={{
                    position: 'top',
                    formatter: (value) => formatCurrency(value),
                    fontSize: 10,
                    fill: '#4A5F7A',
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* PDF Modal - Update with smart date handling */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#0F1A3A] flex items-center gap-2">
                <FaFilePdf className="text-[#C9A84C]" />
                Download PDF Report
              </h2>
              <button
                onClick={() => setShowPdfModal(false)}
                className="text-[#6B7FA0] hover:text-[#0F1A3A] transition p-2 rounded-lg hover:bg-[#F0F2F5]"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                  <FaCalendarAlt size={12} />
                  Period
                </label>
                <select
                  value={pdfPeriod}
                  onChange={(e) => setPdfPeriod(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {pdfPeriod === 'custom' ? (
                <>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={pdfStartDate}
                      onChange={(e) => setPdfStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      End Date
                    </label>
                    <input
                      type="date"
                      value={pdfEndDate}
                      onChange={(e) => setPdfEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                </>
              ) : pdfPeriod === 'daily' ? (
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Date
                  </label>
                  <input
                    type="date"
                    value={pdfStartDate}
                    onChange={(e) => setPdfStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                  />
                </div>
              ) : pdfPeriod === 'weekly' ? (
                <>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week Start
                    </label>
                    <input
                      type="date"
                      value={pdfStartDate}
                      onChange={(e) => setPdfStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week End
                    </label>
                    <input
                      type="date"
                      value={pdfEndDate}
                      onChange={(e) => setPdfEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Year
                    </label>
                    <select
                      value={pdfYear}
                      onChange={(e) => setPdfYear(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                        (y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  {pdfPeriod === 'monthly' && (
                    <div>
                      <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                        <FaCalendarAlt size={12} />
                        Month
                      </label>
                      <select
                        value={pdfMonth}
                        onChange={(e) => setPdfMonth(parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                      >
                        {[
                          'Jan',
                          'Feb',
                          'Mar',
                          'Apr',
                          'May',
                          'Jun',
                          'Jul',
                          'Aug',
                          'Sep',
                          'Oct',
                          'Nov',
                          'Dec',
                        ].map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {userEmail && (
                <div className="bg-[#F5F7FA] rounded-xl p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#4A5F7A]">User</span>
                    <span className="font-medium text-[#0F1A3A] truncate max-w-[180px]">
                      {userEmail}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPdfModal(false)}
                className="flex-1 py-3 border-2 border-[#E8ECF2] rounded-xl hover:bg-[#F5F7FA] transition text-sm font-medium text-[#0F1A3A]"
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={loading}
                className="flex-1 py-3 bg-[#0F1A3A] text-white rounded-xl hover:bg-[#1B2F5C] transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 shadow-lg"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                {loading ? 'Generating...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Modal - Update with smart date handling */}
      {showExcelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#0F1A3A] flex items-center gap-2">
                <FaFileExcel className="text-green-600" />
                Download Excel Report
              </h2>
              <button
                onClick={() => setShowExcelModal(false)}
                className="text-[#6B7FA0] hover:text-[#0F1A3A] transition p-2 rounded-lg hover:bg-[#F0F2F5]"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                  <FaCalendarAlt size={12} />
                  Period
                </label>
                <select
                  value={excelPeriod}
                  onChange={(e) => setExcelPeriod(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {excelPeriod === 'custom' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={excelStartDate}
                      onChange={(e) => setExcelStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      End Date
                    </label>
                    <input
                      type="date"
                      value={excelEndDate}
                      onChange={(e) => setExcelEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                </div>
              ) : excelPeriod === 'daily' ? (
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Date
                  </label>
                  <input
                    type="date"
                    value={excelStartDate}
                    onChange={(e) => setExcelStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                  />
                </div>
              ) : excelPeriod === 'weekly' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week Start
                    </label>
                    <input
                      type="date"
                      value={excelStartDate}
                      onChange={(e) => setExcelStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week End
                    </label>
                    <input
                      type="date"
                      value={excelEndDate}
                      onChange={(e) => setExcelEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Year
                    </label>
                    <select
                      value={excelYear}
                      onChange={(e) => setExcelYear(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                        (y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  {excelPeriod === 'monthly' && (
                    <div>
                      <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                        <FaCalendarAlt size={12} />
                        Month
                      </label>
                      <select
                        value={excelMonth}
                        onChange={(e) => setExcelMonth(parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium"
                      >
                        {[
                          'Jan',
                          'Feb',
                          'Mar',
                          'Apr',
                          'May',
                          'Jun',
                          'Jul',
                          'Aug',
                          'Sep',
                          'Oct',
                          'Nov',
                          'Dec',
                        ].map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Excel Filters Section */}
              <div className="border-t border-[#E8ECF2] pt-4">
                <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-3">
                  <FaFilter size={12} />
                  Filters
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Room Type</label>
                    <select
                      value={excelRoomType}
                      onChange={(e) => setExcelRoomType(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Types</option>
                      {roomTypeStats.map((type) => (
                        <option key={type.room_type} value={type.room_type}>
                          {type.room_type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Room Status</label>
                    <select
                      value={excelRoomStatus}
                      onChange={(e) => setExcelRoomStatus(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Status</option>
                      <option value="Occupied">Occupied</option>
                      <option value="Available">Available</option>
                      <option value="Reserved">Reserved</option>
                      <option value="Under Maintenance">Under Maintenance</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Booking Status</label>
                    <select
                      value={excelBookingStatus}
                      onChange={(e) => setExcelBookingStatus(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Booking Status</option>
                      <option value="Soft">Reserved</option>
                      <option value="Hard">Checked In</option>
                      <option value="Checkedout">Checked Out</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Booking Type</label>
                    <select
                      value={excelBookingType}
                      onChange={(e) => setExcelBookingType(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Booking Types</option>
                      <option value="Walkin">Walkin</option>
                      <option value="Online">Online</option>
                    </select>
                  </div>
                </div>
              </div>

              {userEmail && (
                <div className="bg-[#F5F7FA] rounded-xl p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#4A5F7A]">User</span>
                    <span className="font-medium text-[#0F1A3A] truncate max-w-[180px]">
                      {userEmail}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowExcelModal(false)}
                className="flex-1 py-3 border-2 border-[#E8ECF2] rounded-xl hover:bg-[#F5F7FA] transition text-sm font-medium text-[#0F1A3A]"
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadExcel}
                disabled={loading}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 shadow-lg hover:shadow-xl"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                {loading ? 'Generating...' : 'Download Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal - Update with smart date handling */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 shadow-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#0F1A3A] flex items-center gap-2">
                <FaEnvelope className="text-[#C9A84C]" />
                Send Report via Email
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-[#6B7FA0] hover:text-[#0F1A3A] transition p-2 rounded-lg hover:bg-[#F0F2F5]"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaEnvelope size={12} />
                    Recipient Email *
                  </label>
                  <input
                    type="email"
                    placeholder="Enter recipient email"
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.value.trim())}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaUser size={12} />
                    Recipient Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter recipient name"
                    value={emailRecipientName}
                    onChange={(e) => setEmailRecipientName(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaUser size={12} />
                    Generated By
                  </label>
                  <input
                    type="text"
                    value={emailGeneratedBy}
                    disabled={true}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl text-sm text-[#0F1A3A] font-medium bg-gray-100 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Period
                  </label>
                  <select
                    value={emailPeriod}
                    onChange={(e) => setEmailPeriod(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaFilePdf size={12} />
                    Export Format
                  </label>
                  <select
                    value={emailExportFormat}
                    onChange={(e) => setEmailExportFormat(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                  >
                    <option value="pdf">PDF</option>
                    <option value="xlsx">Excel (XLSX)</option>
                  </select>
                </div>
              </div>

              {emailPeriod === 'custom' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={emailStartDate}
                      onChange={(e) => setEmailStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      End Date
                    </label>
                    <input
                      type="date"
                      value={emailEndDate}
                      onChange={(e) => setEmailEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                    />
                  </div>
                </div>
              ) : emailPeriod === 'daily' ? (
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Date
                  </label>
                  <input
                    type="date"
                    value={emailStartDate}
                    onChange={(e) => setEmailStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                  />
                </div>
              ) : emailPeriod === 'weekly' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week Start
                    </label>
                    <input
                      type="date"
                      value={emailStartDate}
                      onChange={(e) => setEmailStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week End
                    </label>
                    <input
                      type="date"
                      value={emailEndDate}
                      onChange={(e) => setEmailEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Year
                    </label>
                    <select
                      value={emailYear}
                      onChange={(e) => setEmailYear(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                        (y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  {emailPeriod === 'monthly' && (
                    <div>
                      <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                        <FaCalendarAlt size={12} />
                        Month
                      </label>
                      <select
                        value={emailMonth}
                        onChange={(e) => setEmailMonth(parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 border-2 border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0F1A3A] font-medium transition-all"
                      >
                        {[
                          'Jan',
                          'Feb',
                          'Mar',
                          'Apr',
                          'May',
                          'Jun',
                          'Jul',
                          'Aug',
                          'Sep',
                          'Oct',
                          'Nov',
                          'Dec',
                        ].map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-[#E8ECF2] pt-4">
                <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-3">
                  <FaFilter size={12} />
                  Additional Filters
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Room Type</label>
                    <select
                      value={emailRoomType}
                      onChange={(e) => setEmailRoomType(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Types</option>
                      {roomTypeStats.map((type) => (
                        <option key={type.room_type} value={type.room_type}>
                          {type.room_type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Room Status</label>
                    <select
                      value={emailRoomStatus}
                      onChange={(e) => setEmailRoomStatus(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Status</option>
                      <option value="Occupied">Occupied</option>
                      <option value="Available">Available</option>
                      <option value="Reserved">Reserved</option>
                      <option value="Under Maintenance">Under Maintenance</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Booking Status</label>
                    <select
                      value={emailBookingStatus}
                      onChange={(e) => setEmailBookingStatus(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Booking Status</option>
                      <option value="Soft">Reserved</option>
                      <option value="Hard">Checked In</option>
                      <option value="Checkedout">Checked Out</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Booking Type</label>
                    <select
                      value={emailBookingType}
                      onChange={(e) => setEmailBookingType(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0F1A3A] font-medium transition-all mt-1"
                    >
                      <option value="all">All Booking Types</option>
                      <option value="Walkin">Walkin</option>
                      <option value="Online">Online</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <div className="w-full">
                      <label className="text-[10px] font-medium text-[#6B7FA0] block mb-1">
                        CC Hotel
                      </label>
                      <button
                        type="button"
                        onClick={() => setEmailCcHotel(!emailCcHotel)}
                        className={`relative w-full h-10 rounded-xl transition-all duration-300 flex items-center px-3 ${
                          emailCcHotel
                            ? 'bg-[#C9A84C] text-white shadow-md'
                            : 'bg-[#E8ECF2] text-[#6B7FA0]'
                        }`}
                      >
                        <span className="flex items-center justify-between w-full">
                          <span className="text-xs font-medium flex items-center gap-2">
                            {emailCcHotel ? (
                              <>
                                <FaCheck size={12} /> CC Enabled
                              </>
                            ) : (
                              <>
                                <FaTimes size={12} /> CC Disabled
                              </>
                            )}
                          </span>
                          <span className="w-6 h-6 rounded-full bg-white shadow-md" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {userEmail && (
                <div className="bg-[#F5F7FA] rounded-xl p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#4A5F7A]">User</span>
                    <span className="font-medium text-[#0F1A3A] truncate max-w-[180px]">
                      {userEmail}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 py-3 border-2 border-[#E8ECF2] rounded-xl hover:bg-[#F5F7FA] transition text-sm font-medium text-[#0F1A3A]"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex-1 py-3 bg-[#C9A84C] text-[#0F1A3A] rounded-xl hover:bg-[#B8973A] transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 shadow-lg hover:shadow-xl"
              >
                {sendingEmail ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                {sendingEmail ? 'Sending...' : 'Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @media print {
          .bg-[#F4F6FA] {
            background: white !important;
          }
          button,
          .shadow-lg,
          .shadow-xl {
            box-shadow: none !important;
          }
          .fixed {
            display: none !important;
          }
          .border {
            border-color: #e8ecf2 !important;
          }
          .bg-gradient-to-r {
            background: #0f1a3a !important;
          }
        }
        * {
          font-family:
            -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
            sans-serif;
        }
        .hover\\:shadow-xl:hover {
          box-shadow: 0 20px 40px rgba(15, 26, 58, 0.12);
        }
        .hover\\:-translate-y-1:hover {
          transform: translateY(-4px);
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default RoomOccupancyReport;
